import { useState, useEffect, useRef } from 'react'
import { X, AlertCircle } from 'lucide-react'
import { LeagueFormat, type League } from '../types'
import styles from './NotificationsSheet.module.css'

type NotificationsSheetProps = {
  league: League
  onClose: () => void
}

type NotifToggle = {
  id: string
  label: string
  description: string
  defaultOn: boolean
  h2hOnly?: boolean
}

const NOTIFICATION_GROUPS: { title: string; items: NotifToggle[] }[] = [
  {
    title: 'Minhas notificações',
    items: [
      { id: 'window_open',   label: 'Janela aberta',          description: 'Quando a janela de transferências abre.',           defaultOn: true  },
      { id: 'window_close',  label: 'Janela fechando (1h)',    description: 'Aviso 1 hora antes do fechamento da janela.',       defaultOn: true  },
      { id: 'round_result',  label: 'Resultado da rodada',     description: 'Seus pontos e posição após cada rodada.',           defaultOn: true  },
      { id: 'eliminated',    label: 'Jogador eliminado',       description: 'Quando um jogador do seu time é eliminado.',        defaultOn: true  },
    ],
  },
  {
    title: 'Notificações opcionais',
    items: [
      { id: 'goal_scored',   label: 'Gol do seu time',         description: 'Notificação a cada gol marcado por seu jogador.',  defaultOn: false },
      { id: 'h2h_live',      label: 'Ao vivo (H2H)',           description: 'Gols do adversário e placar no intervalo.',        defaultOn: false, h2hOnly: true },
    ],
  },
]

export function NotificationsSheet({ league, onClose }: NotificationsSheetProps) {
  const isH2H = league.format === LeagueFormat.HeadToHead
  const sheetRef = useRef<HTMLDivElement>(null)

  const initialState = Object.fromEntries(
    NOTIFICATION_GROUPS.flatMap((g) => g.items).map((item) => [item.id, item.defaultOn]),
  )
  const [toggles, setToggles] = useState<Record<string, boolean>>(initialState)

  // Close on Escape
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  // Focus trap
  useEffect(() => {
    sheetRef.current?.focus()
  }, [])

  function toggle(id: string) {
    setToggles((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  return (
    <div className={styles.backdrop} onClick={onClose} role="dialog" aria-modal="true" aria-label="Notificações da liga">
      <div
        ref={sheetRef}
        className={styles.sheet}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
        outline-none=""
      >
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.title}>Notificações</span>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">
            <X size={16} />
          </button>
        </div>

        {/* Global opt-out warning — TODO: read from appStore when setting exists */}
        <div className={styles.body}>
          <div className={styles.globalWarning}>
            <AlertCircle size={14} />
            <p>
              Notificações push requerem permissão do navegador.{' '}
              <span className={styles.warningLink}>Configurar nas preferências</span>
            </p>
          </div>

          {NOTIFICATION_GROUPS.map((group) => {
            const visibleItems = group.items.filter(
              (item) => !item.h2hOnly || isH2H,
            )
            if (visibleItems.length === 0) return null
            return (
              <div key={group.title} className={styles.group}>
                <p className={styles.groupTitle}>{group.title}</p>
                {visibleItems.map((item) => (
                  <label key={item.id} className={styles.notifRow}>
                    <div className={styles.notifInfo}>
                      <span className={styles.notifLabel}>{item.label}</span>
                      <span className={styles.notifDesc}>{item.description}</span>
                    </div>
                    <button
                      role="switch"
                      aria-checked={toggles[item.id]}
                      className={styles.toggle}
                      data-on={toggles[item.id]}
                      onClick={() => toggle(item.id)}
                      aria-label={item.label}
                    >
                      <span className={styles.toggleThumb} />
                    </button>
                  </label>
                ))}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
